import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, XCircle, Package, Clock, Truck, MapPin, FileText } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../context/CartContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OrderSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const orderId = searchParams.get('order_id');
  const paymentType = searchParams.get('payment');
  const { clearCart } = useCart();
  const [status, setStatus] = useState('checking'); // checking, success, failed
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);

  useEffect(() => {
    if (paymentType === 'cod' && orderId) {
      // COD order - fetch order details directly
      fetchOrderDetails();
    } else if (sessionId) {
      // Online payment - check payment status
      checkPaymentStatus();
    }
  }, [sessionId, orderId, paymentType]);

  const fetchOrderDetails = async () => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}`);
      setOrderDetails(response.data);
      setStatus('success');
      clearCart();
    } catch (error) {
      console.error('Error fetching order:', error);
      setStatus('failed');
    }
  };

  const checkPaymentStatus = async () => {
    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setPaymentDetails(response.data);
        clearCart();
      } else if (response.data.status === 'expired') {
        setStatus('failed');
      } else {
        // Continue polling
        setTimeout(checkPaymentStatus, 2000);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('failed');
    }
  };

  if (status === 'checking') {
    return (
      <div className="section-spacing" data-testid="checking-status">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center space-y-8">
            <Loader2 className="w-24 h-24 mx-auto text-primary animate-spin" />
            <h2 className="text-3xl font-heading font-bold">Processing Your Order...</h2>
            <p className="text-lg text-muted-foreground">
              Please wait while we confirm your order.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="section-spacing" data-testid="payment-failed">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center space-y-8">
            <XCircle className="w-24 h-24 mx-auto text-destructive" />
            <h2 className="text-3xl font-heading font-bold">Order Failed</h2>
            <p className="text-lg text-muted-foreground">
              Your order could not be processed. Please try again.
            </p>
            <Link to="/checkout">
              <button className="btn-primary" data-testid="retry-payment-button">
                Try Again
              </button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const isCOD = paymentType === 'cod' || orderDetails?.payment_method === 'cod';

  return (
    <div className="section-spacing" data-testid="order-success-page">
      <div className="container-custom">
        <div className="max-w-3xl mx-auto space-y-8">
          {/* Success Header */}
          <div className="text-center space-y-4">
            <CheckCircle className="w-24 h-24 mx-auto text-primary" data-testid="success-icon" />
            <h1 className="text-4xl md:text-5xl font-heading font-bold" data-testid="success-heading">
              Order Placed Successfully!
            </h1>
            <p className="text-lg text-muted-foreground">
              {isCOD 
                ? "Thank you for your order! Payment will be collected on delivery."
                : "Thank you for your order! We've sent a confirmation email with your order details."
              }
            </p>
          </div>

          {/* Order Details Card */}
          <div className="bg-white rounded-2xl p-8 shadow-card" data-testid="order-details">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-heading font-bold">Order Details</h3>
              {(orderId || orderDetails?.id) && (
                <span className="text-sm bg-primary/10 text-primary px-3 py-1 rounded-full">
                  Order #{(orderId || orderDetails?.id)?.slice(0, 8).toUpperCase()}
                </span>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Payment Info */}
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    {isCOD ? <Clock className="w-5 h-5 text-primary" /> : <CheckCircle className="w-5 h-5 text-primary" />}
                  </div>
                  <div>
                    <p className="font-medium">Payment Status</p>
                    <p className="text-sm text-muted-foreground">
                      {isCOD ? 'Payment Pending (Cash on Delivery)' : 'Payment Successful'}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Package className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">Order Status</p>
                    <p className="text-sm text-muted-foreground">
                      {orderDetails?.order_status === 'confirmed' ? 'Confirmed' : 'Processing'}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Amount Info */}
              <div className="space-y-3">
                {paymentDetails && (
                  <div className="flex justify-between py-2 border-b border-border">
                    <span className="text-muted-foreground">Amount</span>
                    <span className="font-semibold">
                      ₹{(paymentDetails.amount_total / 100).toFixed(2)}
                    </span>
                  </div>
                )}
                {orderDetails && (
                  <>
                    {orderDetails.discount_amount > 0 && (
                      <div className="flex justify-between py-2 border-b border-border text-green-600">
                        <span>Discount Applied</span>
                        <span className="font-semibold">-₹{orderDetails.discount_amount.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between py-2 border-b border-border">
                      <span className="text-muted-foreground">Total Amount</span>
                      <span className="font-bold text-lg text-primary">
                        ₹{orderDetails.total_amount.toFixed(2)}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* What's Next */}
          <div className="bg-white rounded-2xl p-8 shadow-card">
            <h3 className="text-xl font-heading font-bold mb-6">What's Next?</h3>
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-xl">
                <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold text-sm">1</div>
                <div>
                  <p className="font-medium">Order Confirmation</p>
                  <p className="text-sm text-muted-foreground">You'll receive an email confirmation shortly</p>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-xl">
                <div className="w-8 h-8 rounded-full bg-muted text-muted-foreground flex items-center justify-center font-bold text-sm">2</div>
                <div>
                  <p className="font-medium">Processing</p>
                  <p className="text-sm text-muted-foreground">We'll prepare your order for shipping</p>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-xl">
                <div className="w-8 h-8 rounded-full bg-muted text-muted-foreground flex items-center justify-center font-bold text-sm">3</div>
                <div>
                  <p className="font-medium">Shipping</p>
                  <p className="text-sm text-muted-foreground">Track your order once it's shipped</p>
                </div>
              </div>
              {isCOD && (
                <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-xl">
                  <div className="w-8 h-8 rounded-full bg-muted text-muted-foreground flex items-center justify-center font-bold text-sm">4</div>
                  <div>
                    <p className="font-medium">Pay on Delivery</p>
                    <p className="text-sm text-muted-foreground">Pay cash when your order arrives</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {(orderId || orderDetails?.id) && (
              <Link to={`/track-order/${orderId || orderDetails?.id}`}>
                <button className="btn-outline flex items-center justify-center gap-2" data-testid="track-order-button">
                  <Truck className="w-5 h-5" />
                  Track Order
                </button>
              </Link>
            )}
            <Link to="/products">
              <button className="btn-primary" data-testid="continue-shopping-button">
                Continue Shopping
              </button>
            </Link>
            <Link to="/dashboard">
              <button className="btn-outline" data-testid="view-orders-button">
                View All Orders
              </button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccess;
